import { icon } from "./icons";

export type ToastKind = "ok" | "warn" | "error";

const look: Record<ToastKind, { icon: string; label: string; ms: number }> = {
  ok: { icon: "check-circle", label: "Done:", ms: 4000 },
  warn: { icon: "warning", label: "Warning:", ms: 6000 },
  error: { icon: "x-circle", label: "Error:", ms: 8000 },
};

function dismiss(el: HTMLElement): void {
  if (el.dataset.leaving) return;
  el.dataset.leaving = "1";
  el.classList.add("is-leaving");
  const gone = () => el.remove();
  el.addEventListener("transitionend", gone, { once: true });
  setTimeout(gone, 400);   // reduced motion: no transition fires
}

/** WP50: stacked toasts with an icon and a close button; hovering one pauses its timer. The text is the server's, unchanged. */
export function toast(kind: ToastKind, text: string): void {
  const host = document.getElementById("toast-host");
  if (!host) return;
  const { icon: name, label, ms } = look[kind] ?? look.ok;
  const el = document.createElement("div");
  el.className = `toast toast-${kind}`;
  // the host is already a polite live region; only an error interrupts (a role on every toast reads them twice)
  if (kind === "error") el.setAttribute("role", "alert");
  const hidden = document.createElement("span");
  hidden.className = "sr-only";
  hidden.textContent = label + " ";
  const body = document.createElement("span");
  body.className = "toast-text";
  body.textContent = text;
  const close = document.createElement("button");
  close.type = "button";
  close.className = "toast-close";
  close.tabIndex = -1;
  close.setAttribute("aria-label", "Dismiss");
  close.appendChild(icon("x"));
  close.addEventListener("click", () => dismiss(el));
  el.append(icon(name, "toast-icon"), hidden, body, close);
  host.appendChild(el);

  let left = ms;
  let started = Date.now();
  let timer = setTimeout(() => dismiss(el), left);
  el.addEventListener("mouseenter", () => { clearTimeout(timer); left -= Date.now() - started; });
  el.addEventListener("mouseleave", () => { started = Date.now(); timer = setTimeout(() => dismiss(el), Math.max(left, 1200)); });
  // keep the stack short: the oldest goes first
  while (host.children.length > 4) dismiss(host.firstElementChild as HTMLElement);
}

export function initToasts() {
  document.body.addEventListener("toast", (evt: Event) => {
    const payload = (evt as CustomEvent).detail;
    if (payload) {
      const text = payload.text || payload.message;
      const kind = (payload.kind || (payload.level === "success" ? "ok" : payload.level) || "ok") as ToastKind;
      if (text) {
        toast(kind, text);
      }
    }
  });
}
